/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {RestResponse, State, Client, User, Stage, Report, CorporateEntity, ReportFile} from '../model';
import {SearchableProvider} from '../model/interface/searchable.provider';
import {ApiHeaders} from './headers';
import {StageChangeRequest} from '../model/report';
import { Tag } from '../model/tag';
import { SystemType } from '../model/enums/system.type';
import { ReportFollowUp } from '../model/report.followup';
import { StatStageType } from '../model/enums/stat.stage.type';
import { PresetValue } from '../model/preset-value';

@Injectable()
export class ReportProvider implements SearchableProvider {

  constructor(
    private http: HttpClient
  ) {}

  getReports(page: number, query?: string, sort?: boolean, order?: string, filter?: string, filterBy?: string, size?: number, qType?: string, type?: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/list?page=${page}&size=${size}&sort=${sort}&order=${order}`,
        {
          TYPE: type,
          QUERY_TYPE: qType,
          QUERY: query,
        },
        ApiHeaders.getOptions());
  }

  getFilteredReports(page: number, size: number, sort: boolean, order: string, type: string, qType?: string,  query?: string, state?: State[], stage?: Stage[],
    assignee?: User[], processor?: User[], researchAnalyst?: User[], financialAnalyst?: User[], seniorAnalyst?: User[],
    bank?: Client[], delayedType?: string, tags?: Tag[],
    eqprocessor?: User[], eqresearchAnalyst?: User[], eqAppraiser?: User[],
    eqReviewer?: User[], misc?: PresetValue[]): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/list?page=${page}&size=${size}&sort=${sort}&order=${order}`,
        {
          TYPE: type,
          QUERY_TYPE: qType,
          QUERY: query,
          STAGE: stage ? stage.map(s => s.ID) : null,
          STATE: state ? state.map(s => s.ID) : null,
          ASSIGNEE: assignee ? assignee.map(u => u.ID) : null,
          PROCESSOR: processor ? processor.map(u => u.ID) : null,
          RESEARCH_ANALYST: researchAnalyst ? researchAnalyst.map(u => u.ID) : null,
          FINANCIAL_ANALYST: financialAnalyst ? financialAnalyst.map(u => u.ID) : null,
          SENIOR_ANALYST: seniorAnalyst ? seniorAnalyst.map(u => u.ID) : null,
          CLIENT: bank ? bank.map(b => b.ID) : null,
          DELAY: delayedType ? delayedType : null,
          TAGS: tags ? tags.map(t => t.NAME) : null,
          EQUIPMENT_PROCESSOR: eqprocessor ? eqprocessor.map(u => u.ID) : null,
          EQUIPMENT_RESEARCH_ANALYST: eqresearchAnalyst ? eqresearchAnalyst.map(u => u.ID) : null,
          EQUIPMENT_APPRAISER: eqAppraiser ? eqAppraiser.map(u => u.ID) : null,
          EQUIPMENT_REVIEWER: eqReviewer ? eqReviewer.map(u => u.ID) : null,
          MISC: misc ? misc.map(m => m.VALUE) : null
        },
        ApiHeaders.getOptions());
  }

  getHistoryReports(page: number, size: number, type: string, period_beg: string, period_end: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/list/history?page=${page}&size=${size}`,
        {
          TYPE: type,
          PERIOD_BEG: period_beg,
          PERIOD_END: period_end,
        },
        ApiHeaders.getOptions());
  }

  getReportById(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/pma/report/${id}`, ApiHeaders.getOptions());
  }

  sendReminder(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/remind`, null, ApiHeaders.getOptions());
  }

  createReport(companyName?: string, stage?: string, type?: any, mode?: SystemType ): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report` + (stage ? `?stage=${stage}` : 'ORDR'), {
        NAME: companyName ? companyName : '',
        REPORT_TYPE: type ? type : 'CONCLUSION',
        TYPE: mode? mode: SystemType.BV
      }, ApiHeaders.getOptions());
  }

  duplicateReport(id: string, entities?: CorporateEntity[], interval_name?: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/duplicate`, {
        ENTITIES: entities? entities: [],
        INTERNAL_FILE_NAME: interval_name? interval_name: ''
      }, ApiHeaders.getOptions());
  }

  deleteReport(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/delete`, null, ApiHeaders.getOptions());
  }

  deleteReports(ids: string[]): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/multiple/delete`, ids, ApiHeaders.getOptions());
  }

  setStage(id: string, stage: StageChangeRequest): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/stage`, stage, ApiHeaders.getOptions());
  }

  engageReport(id: string, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/engage?type=${type}`, null, ApiHeaders.getOptions());
  }

  resendDs(id: string, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/resend/ds?type=${type}`, null, ApiHeaders.getOptions());
  }

  resendManual(id: string, body: any, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/resend/manual?type=${type}`, body, ApiHeaders.getOptions());
  }

  engageReportManual(id: string, body: any, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/engage/manual?type=${type}`, body, ApiHeaders.getOptions());
  }

  engageReportFile(id: string, subject: string, email: string, type: SystemType, cc:string[], bcc: string[]): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/engage/file?type=${type}`,
        {subject: subject, email: email, cc: cc, bcc: bcc}, ApiHeaders.getOptions());
  }

  siteVisitManual(id: string, body: any, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/site-visit/manual?type=${type}`, body, ApiHeaders.getOptions());
  }

  getSiteVisitDefaultEmailContent(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/site-visit/default-email-content`, ApiHeaders.getOptions());
  }

  getEngagementLetterPreview(id: string, type: SystemType): Observable<any> {
    return this.http
      .get(`${environment.API_URL}/api/pma/report/${id}/el-preview?type=${type}`, { responseType: 'arraybuffer' });
  }

  getDocuSignDocument(id: string): Observable<any> {
    return this.http
      .get(`${environment.API_URL}/api/docusign/${id}/download`, ApiHeaders.getOptionsPlain());
  }

  getDocuSignDocumentPreview(id: string): Observable<any> {
    return this.http
      .get(`${environment.API_URL}/api/docusign/${id}/preview`, ApiHeaders.getOptions());
  }

  getManualDocumentPreview(id: string, type: SystemType): Observable<any> {
    return this.http
      .get(`${environment.API_URL}/api/pma/report/${id}/el-preview-image?type=${type}`, ApiHeaders.getOptions());
  }

  setReportUser(id: string, type: string, uid?: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/user`, {
        FIELD: type,
        VALUE: uid
      }, ApiHeaders.getOptions());
  }

  setClientUser(id: string, type: string, clUserId?: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user`, {
        FIELD: type,
        VALUE: clUserId
      }, ApiHeaders.getOptions());
  }

  setClientUserToggle(id: string, clUserId: string, toggle: string, value: boolean): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user/${clUserId}/toggle?toggle=${toggle}&value=${value}`,
        null, ApiHeaders.getOptions());
  }

  createClientUserOther(id: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user/other`, null, ApiHeaders.getOptions());
  }

  deleteClientUserOther(id: string, cu: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user/other/${cu}`, ApiHeaders.getOptions());
  }

  setClientUserOther(id: string, cu: string, usr?: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user/other/${cu}/user/${usr ? usr : ''}`,
        null, ApiHeaders.getOptions());
  }

  setClientUserOtherLabel(id: string, cu: string, label: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user/other/${cu}?label=${label}`,
        null, ApiHeaders.getOptions());
  }

  sendRequestInformationWithAttachment(dataRequest: FormData): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/email/docs`, dataRequest);
  }

  identifyZip(zip: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/zip/identify/${zip}`, ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'REPORT';
  }

  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  saveFields(id: string, fieldValues: any[]): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/fields`, fieldValues, ApiHeaders.getOptions());
  }

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number, position?: string, type?: SystemType): Observable<RestResponse> {
    return this.getReports(page ? page : 1, query, true, 'CREATED_DATE', null, null, 25, 'COMPANY_NAME', type);
  }

  cancelReport(id: string, reason: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/cancel`, {
        FIELD: 'REASON',
        VALUE: reason
      }, ApiHeaders.getOptions());
  }

  revertReport(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/revert`, ApiHeaders.getOptions());
  }

  getClientLender(id: string, cid: number):  Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client/${cid}/lender`, ApiHeaders.getOptions());
  }

  saveFee(id: string, charge: number, desc: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/field/fee`, {
        CHARGE: charge,
        CHARGE_DESC: desc
      }, ApiHeaders.getOptions());
  }

  assignToBank(id: string, cid: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client/${cid}/assign`, {}, ApiHeaders.getOptions());

  }

  saveDbaNames(id: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/entity/mutltiple`, {}, ApiHeaders.getOptions());
  }

  getReportUsers(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/users`, ApiHeaders.getOptions());
  }

  getReportDelay(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/active/delay`, ApiHeaders.getOptions());
  }

  exportReport(page?: number): Observable<any> {
    return this.http
      .get(`${environment.API_URL}/api/pma/report/export/all?page=${page}`, ApiHeaders.getOptionsPlain());
  }

  saveEngagement(id: string, value: any, type: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/field/engagement?type=${type}`, {
        FIELD: 'DATE_ENGAGED',
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  createClientUserClientOther(id: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/client-user/other/client`, null, ApiHeaders.getOptions());
  }

  merge(id: string, appId: string, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/merge?type=${type}`, {
        FIELD: 'ID',
        VALUE: appId
      }, ApiHeaders.getOptions());
  }

  linkReport(id: string, targetId: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/link_report/${targetId}`, null, ApiHeaders.getOptions());
  }

  unlinkReport(id: string, targetId: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/link_report/${targetId}`, ApiHeaders.getOptions());
  }

  link(id: string, appId: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/link`, {
        FIELD: 'ID',
        VALUE: appId
      }, ApiHeaders.getOptions());
  }

  updateReportStage(id: string, stageId): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/stage/${stageId}`, {}, ApiHeaders.getOptions());
  }

  updateSov(id: string, values: string[]): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/field/sov`, {
        FIELD: 'STANDARD_OF_VALUES',
        VALUES: values
      }, ApiHeaders.getOptions());
  }

  convert(report: string, type: SystemType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/convert`, {
        REPORT: report,
        TYPE: type
      }, ApiHeaders.getOptions());
  }

  convertMerge(report: string, type: SystemType, targetReport: String): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/convert-merge`, {
        REPORT: report,
        TYPE: type,
        TARGET_REPORT: targetReport
      }, ApiHeaders.getOptions());
  }

  sendFollowUp(id: string, followUp: ReportFollowUp): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/follow-up`, followUp, ApiHeaders.getOptions());
  }

  getReportFiles(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files`, ApiHeaders.getOptions());
  }

  addReportFiles(id: string, formData: FormData, parentId: string): Observable<any> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files/${parentId}/add`, formData);
  }

  downloadFile(id: string, path: string, fileId: string): Observable<any> {
    return this.http.post(`${environment.API_URL}/api/pma/report/${id}/files/${fileId}/download`, path, ApiHeaders.getOptionsPlain());
  }

  downloadZip(id: string, path: string, selected: any[]): Observable<any> {
    return this.http.post(`${environment.API_URL}/api/pma/report/${id}/files/zip`, {
      PATH: path,
      FILES: selected
    }, ApiHeaders.getOptionsJsonPlain());
  }

  deleteAll(id: string, path: string, selected: any[]): Observable<RestResponse> {
    return this.http.post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files/delete`, {
      PATH: path,
      FILES: selected
    }, ApiHeaders.getOptions());
  }

  previewFile(id: string, path: string, fileId: string): Observable<any> {
    return this.http.post(`${environment.API_URL}/api/pma/report/${id}/files/${fileId}/preview`, path, { responseType: 'arraybuffer' });
  }

  deleteFile(id: string, path: string, fileId: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files/${fileId}/delete`, path, ApiHeaders.getOptions());
  }

  createFolder(id: string, name: string, fileId: string): Observable<RestResponse> {
    return this.http.post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files/folder${fileId ? '?parent=' + fileId : ''}`, name, ApiHeaders.getOptions());
  }

  renameFileOrFolder(id: string, path: string, name: string, fileId: string): Observable<RestResponse> {
    return this.http.put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files/${fileId}`, {
      PATH: path,
      NAME: name
    }, ApiHeaders.getOptions());
  }

  changeDocType(id: string, type: string, fileId: string): Observable<RestResponse> {
    return this.http.put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/files/${fileId}/type`, {
      TYPE: type
    }, ApiHeaders.getOptions());
  }

  uploadFiles(id: string, formData: ReportFile[], parentId: string, path: string): Observable<any> {
    return this.http
      .post<any>(`${environment.API_URL}/api/pma/report/${id}/files/${parentId}/add?path=${path}`, formData, {
        reportProgress: true,
        observe: 'events'
      });
  }

  archiveReport(days: number): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/pma/report/archive?beforeDays=${days}`, null, ApiHeaders.getOptions());
  }

}
